import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { getBookingCommercialState } from './booking';
import {
  notifyBookingCoverageApplied,
  notifyCreditsLow,
  notifyMembershipExpiringSoon,
  notifyPackageAssigned,
  notifyPackageCoverageExhausted,
} from './notifications';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeNumber(value, fallback = 0) {
  if (value === '' || value === null || typeof value === 'undefined') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeArray(values = []) {
  if (Array.isArray(values)) {
    return values.map((value) => normalizeString(value)).filter(Boolean);
  }
  return normalizeString(values)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function toCurrencyCode(value = 'USD') {
  return normalizeString(value || 'USD').toUpperCase() || 'USD';
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isActiveWindow(startAt = null, endAt = null, now = new Date()) {
  const start = toDateValue(startAt);
  const end = toDateValue(endAt);
  if (start && start.getTime() > now.getTime()) return false;
  if (end && end.getTime() < now.getTime()) return false;
  return true;
}

export function normalizeServicePackage(record = {}, fallbackId = '') {
  const id = record.id || record.servicePackageId || fallbackId || '';
  return {
    id,
    servicePackageId: id,
    organizationId: record.organizationId || '',
    name: normalizeString(record.name || ''),
    description: normalizeString(record.description || ''),
    packageType: normalizeString(record.packageType || 'session_bundle'),
    active: record.active !== false,
    includedSessionCount: normalizeNumber(record.includedSessionCount, 0),
    includedCredits: normalizeNumber(record.includedCredits, 0),
    validityDays: normalizeNumber(record.validityDays, 0),
    price: normalizeNumber(record.price, 0),
    currency: toCurrencyCode(record.currency || 'USD'),
    applicableDisciplines: normalizeArray(record.applicableDisciplines || []),
    applicableServiceTypes: normalizeArray(record.applicableServiceTypes || []),
    bookingCoverageType: normalizeString(record.bookingCoverageType || 'full_session'),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function normalizeCustomerMembership(record = {}, fallbackId = '') {
  const id = record.id || record.customerMembershipId || fallbackId || '';
  return {
    id,
    customerMembershipId: id,
    organizationId: record.organizationId || '',
    userId: record.userId || '',
    packageId: record.packageId || record.membershipPlanId || '',
    status: normalizeString(record.status || 'active'),
    startAt: record.startAt || null,
    endAt: record.endAt || null,
    includedSessions: normalizeNumber(record.includedSessions, 0),
    remainingSessions: normalizeNumber(record.remainingSessions, normalizeNumber(record.includedSessions, 0)),
    includedCredits: normalizeNumber(record.includedCredits, 0),
    remainingCredits: normalizeNumber(record.remainingCredits, normalizeNumber(record.includedCredits, 0)),
    autoRenew: Boolean(record.autoRenew),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function normalizePackageUsage(record = {}, fallbackId = '') {
  const id = record.id || record.packageUsageId || fallbackId || '';
  return {
    id,
    packageUsageId: id,
    organizationId: record.organizationId || '',
    userId: record.userId || '',
    bookingId: record.bookingId || '',
    bookingRequestId: record.bookingRequestId || '',
    packageId: record.packageId || '',
    customerMembershipId: record.customerMembershipId || '',
    usageType: normalizeString(record.usageType || 'booking_coverage'),
    sessionsConsumed: normalizeNumber(record.sessionsConsumed, 0),
    creditsConsumed: normalizeNumber(record.creditsConsumed, 0),
    amountCovered: normalizeNumber(record.amountCovered, 0),
    status: normalizeString(record.status || 'applied'),
    createdAt: record.createdAt || null,
    releasedAt: record.releasedAt || null,
    createdByUserId: record.createdByUserId || '',
  };
}

export async function createServicePackage(
  db,
  {
    organizationId = '',
    packageData = {},
  } = {}
) {
  const normalized = normalizeServicePackage({
    ...packageData,
    organizationId,
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.SERVICE_PACKAGES), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  return normalizeServicePackage(normalized, docRef.id || '');
}

export async function updateServicePackage(db, servicePackageId = '', updates = {}) {
  if (!servicePackageId) throw new Error('Service package id is required.');
  const normalized = normalizeServicePackage(updates, servicePackageId);
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.SERVICE_PACKAGES, servicePackageId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  });
  return normalized;
}

export async function getOrganizationServicePackages(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.SERVICE_PACKAGES,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeServicePackage(record));
}

export async function assignPackageToUser(
  db,
  {
    organizationId = '',
    userId = '',
    packageId = '',
    autoRenew = false,
    startAt = null,
    endAt = null,
    actorUserId = '',
    actorName = '',
    membershipData = {},
  } = {}
) {
  if (!organizationId || !userId || !packageId) {
    throw new Error('organizationId, userId, and packageId are required.');
  }

  const packageSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.SERVICE_PACKAGES, packageId));
  if (!packageSnapshot.exists()) {
    throw new Error('Service package not found.');
  }

  const servicePackage = normalizeServicePackage({ id: packageSnapshot.id, ...packageSnapshot.data() });
  const normalized = normalizeCustomerMembership({
    organizationId,
    userId,
    packageId,
    status: 'active',
    startAt,
    endAt,
    includedSessions: membershipData.includedSessions ?? servicePackage.includedSessionCount,
    remainingSessions: membershipData.remainingSessions ?? servicePackage.includedSessionCount,
    includedCredits: membershipData.includedCredits ?? servicePackage.includedCredits,
    remainingCredits: membershipData.remainingCredits ?? servicePackage.includedCredits,
    autoRenew,
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.CUSTOMER_MEMBERSHIPS), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  const membership = normalizeCustomerMembership(normalized, docRef.id || '');

  try {
    await notifyPackageAssigned(db, {
      membership,
      servicePackage,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Package-assigned notification failed:', error);
  }

  return membership;
}

export async function getOrganizationCustomerMemberships(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.CUSTOMER_MEMBERSHIPS,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeCustomerMembership(record));
}

export async function getUserActivePackages(db, userId = '', { organizationId = '' } = {}) {
  if (!userId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.CUSTOMER_MEMBERSHIPS,
    field: 'userId',
    value: userId,
  });

  return records
    .map((record) => normalizeCustomerMembership(record))
    .filter((membership) => membership.status === 'active')
    .filter((membership) => !organizationId || membership.organizationId === organizationId)
    .filter((membership) => isActiveWindow(membership.startAt, membership.endAt));
}

export async function getOrganizationPackageUsages(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.PACKAGE_USAGES,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizePackageUsage(record));
}

export async function getUserPackageUsages(db, userId = '') {
  if (!userId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.PACKAGE_USAGES,
    field: 'userId',
    value: userId,
  });
  return records.map((record) => normalizePackageUsage(record));
}

function packageAppliesToBooking(servicePackage = {}, booking = {}) {
  const normalizedPackage = normalizeServicePackage(servicePackage);
  if (!normalizedPackage.active) return false;
  if (
    normalizedPackage.applicableDisciplines.length > 0 &&
    booking.discipline &&
    !normalizedPackage.applicableDisciplines.includes(booking.discipline)
  ) {
    return false;
  }
  if (
    normalizedPackage.applicableServiceTypes.length > 0 &&
    booking.serviceType &&
    !normalizedPackage.applicableServiceTypes.includes(booking.serviceType)
  ) {
    return false;
  }
  return true;
}

export function getUserRemainingCoverage(
  memberships = [],
  {
    servicePackages = [],
    booking = {},
  } = {}
) {
  const packagesById = (servicePackages || []).reduce((acc, servicePackage) => {
    const normalized = normalizeServicePackage(servicePackage);
    acc[normalized.servicePackageId] = normalized;
    return acc;
  }, {});

  const activeMemberships = (memberships || [])
    .map((membership) => normalizeCustomerMembership(membership))
    .filter((membership) => membership.status === 'active')
    .filter((membership) => isActiveWindow(membership.startAt, membership.endAt))
    .filter((membership) => {
      const servicePackage = packagesById[membership.packageId];
      if (!servicePackage) return true;
      return packageAppliesToBooking(servicePackage, booking);
    });

  return {
    activeMemberships,
    remainingSessions: activeMemberships.reduce((sum, membership) => sum + membership.remainingSessions, 0),
    remainingCredits: activeMemberships.reduce((sum, membership) => sum + membership.remainingCredits, 0),
  };
}

export function getBookingPackageCoverageSummary(
  booking = {},
  {
    memberships = [],
    servicePackages = [],
  } = {}
) {
  const commercialState = getBookingCommercialState(booking);
  const coverageAmount = normalizeNumber(commercialState.packageCoverageAmount, 0);
  const bookingAmount = normalizeNumber(commercialState.priceSnapshot, 0);

  if (commercialState.packageId || coverageAmount > 0) {
    return {
      covered: coverageAmount >= bookingAmount && bookingAmount > 0,
      partial: coverageAmount > 0 && coverageAmount < bookingAmount,
      coverageAmount,
      packageId: commercialState.packageId || '',
      customerMembershipId: commercialState.customerMembershipId || '',
      status: normalizeString(commercialState.packageCoverageStatus || (coverageAmount >= bookingAmount ? 'full' : coverageAmount > 0 ? 'partial' : 'none')),
      remainingAmountDue: Math.max(Number((bookingAmount - coverageAmount).toFixed(2)), 0),
    };
  }

  const remainingCoverage = getUserRemainingCoverage(memberships, { servicePackages, booking });
  const firstMembership = remainingCoverage.activeMemberships[0] || null;
  const firstPackage = firstMembership ? servicePackages.find((servicePackage) => normalizeServicePackage(servicePackage).servicePackageId === firstMembership.packageId) : null;

  return {
    covered: false,
    partial: false,
    coverageAmount: 0,
    packageId: firstPackage ? normalizeServicePackage(firstPackage).servicePackageId : '',
    customerMembershipId: firstMembership?.customerMembershipId || '',
    status: 'none',
    remainingAmountDue: bookingAmount,
    remainingSessions: remainingCoverage.remainingSessions,
    remainingCredits: remainingCoverage.remainingCredits,
  };
}

async function updateCustomerMembershipRemaining(
  db,
  membership = {},
  {
    remainingSessions = null,
    remainingCredits = null,
  } = {}
) {
  const normalizedMembership = normalizeCustomerMembership(membership);
  const updatePayload = {
    updatedAt: serverTimestamp(),
  };
  if (remainingSessions !== null) updatePayload.remainingSessions = remainingSessions;
  if (remainingCredits !== null) updatePayload.remainingCredits = remainingCredits;

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.CUSTOMER_MEMBERSHIPS, normalizedMembership.customerMembershipId), updatePayload);

  return {
    ...normalizedMembership,
    remainingSessions: remainingSessions ?? normalizedMembership.remainingSessions,
    remainingCredits: remainingCredits ?? normalizedMembership.remainingCredits,
  };
}

export async function consumePackageForBooking(
  db,
  {
    booking = {},
    membership = {},
    servicePackage = {},
    actorUserId = '',
  } = {}
) {
  const normalizedMembership = normalizeCustomerMembership(membership);
  const normalizedPackage = normalizeServicePackage(servicePackage);
  const bookingState = getBookingCommercialState(booking);
  const bookingAmount = normalizeNumber(bookingState.priceSnapshot, 0);

  const existingUsages = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.PACKAGE_USAGES,
    field: 'bookingId',
    value: booking.bookingId || booking.id || '',
  });
  const activeExistingUsage = existingUsages
    .map((usage) => normalizePackageUsage(usage))
    .find((usage) => usage.customerMembershipId === normalizedMembership.customerMembershipId && usage.status === 'applied');
  if (activeExistingUsage) {
    return {
      duplicate: true,
      usage: activeExistingUsage,
      amountCovered: activeExistingUsage.amountCovered,
      sessionsConsumed: activeExistingUsage.sessionsConsumed,
      creditsConsumed: activeExistingUsage.creditsConsumed,
    };
  }

  let sessionsConsumed = 0;
  let creditsConsumed = 0;
  let amountCovered = 0;

  if (['session_bundle', 'membership', 'trial_bundle'].includes(normalizedPackage.packageType)) {
    if (normalizedMembership.remainingSessions <= 0) {
      throw new Error('No remaining sessions are available for this package.');
    }
    sessionsConsumed = 1;
    amountCovered = bookingAmount;
  } else {
    if (normalizedMembership.remainingCredits <= 0) {
      throw new Error('No remaining credits are available for this package.');
    }
    creditsConsumed = Math.min(normalizedMembership.remainingCredits, bookingAmount);
    amountCovered = creditsConsumed;
  }

  const usagePayload = normalizePackageUsage({
    organizationId: normalizedMembership.organizationId,
    userId: normalizedMembership.userId,
    bookingId: booking.bookingId || booking.id || '',
    bookingRequestId: booking.bookingRequestId || '',
    packageId: normalizedPackage.servicePackageId,
    customerMembershipId: normalizedMembership.customerMembershipId,
    usageType: 'booking_coverage',
    sessionsConsumed,
    creditsConsumed,
    amountCovered,
    status: 'applied',
    createdByUserId: actorUserId,
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.PACKAGE_USAGES), {
    ...usagePayload,
    createdAt: serverTimestamp(),
  })) || {};

  await updateCustomerMembershipRemaining(db, normalizedMembership, {
    remainingSessions: Math.max(normalizedMembership.remainingSessions - sessionsConsumed, 0),
    remainingCredits: Math.max(normalizedMembership.remainingCredits - creditsConsumed, 0),
  });

  return {
    duplicate: false,
    usage: normalizePackageUsage(usagePayload, docRef.id || ''),
    amountCovered,
    sessionsConsumed,
    creditsConsumed,
  };
}

export async function applyPackageCoverageToBooking(
  db,
  {
    booking = {},
    memberships = [],
    servicePackages = [],
    membershipId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedMemberships = (memberships || []).map((membership) => normalizeCustomerMembership(membership));
  const normalizedPackages = (servicePackages || []).map((servicePackage) => normalizeServicePackage(servicePackage));
  const candidateMembership = membershipId
    ? normalizedMemberships.find((membership) => membership.customerMembershipId === membershipId)
    : getUserRemainingCoverage(normalizedMemberships, {
        servicePackages: normalizedPackages,
        booking,
      }).activeMemberships[0];

  if (!candidateMembership) {
    throw new Error('No eligible package coverage is available for this booking.');
  }

  if (booking.serviceId && booking.servicePackageEligible === false) {
    throw new Error('This service is not eligible for package coverage.');
  }

  const servicePackage = normalizedPackages.find((item) => item.servicePackageId === candidateMembership.packageId);
  if (!servicePackage) {
    throw new Error('The selected package could not be resolved.');
  }

  if (
    booking.serviceId &&
    Array.isArray(booking.applicablePackageIds) &&
    booking.applicablePackageIds.length > 0 &&
    !booking.applicablePackageIds.includes(candidateMembership.packageId)
  ) {
    throw new Error('The selected package is not eligible for this service.');
  }

  const consumption = await consumePackageForBooking(db, {
    booking,
    membership: candidateMembership,
    servicePackage,
    actorUserId,
  });

  const bookingAmount = normalizeNumber(getBookingCommercialState(booking).priceSnapshot, 0);
  const amountCovered = consumption.amountCovered;
  const outstandingBalance = Math.max(Number((bookingAmount - amountCovered).toFixed(2)), 0);
  const packageCoverageStatus = outstandingBalance <= 0 ? 'full' : 'partial';

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, booking.bookingId || booking.id || ''), {
    billingSourceType: 'package',
    billingSourceId: candidateMembership.customerMembershipId,
    packageId: servicePackage.servicePackageId,
    customerMembershipId: candidateMembership.customerMembershipId,
    packageCoverageAmount: amountCovered,
    packageCoverageStatus,
    paymentStatus: outstandingBalance <= 0 ? 'paid' : 'partially_paid',
    outstandingBalance,
    updatedAt: serverTimestamp(),
  });

  try {
    await notifyBookingCoverageApplied(db, {
      booking: {
        ...booking,
        packageId: servicePackage.servicePackageId,
        customerMembershipId: candidateMembership.customerMembershipId,
        packageCoverageAmount: amountCovered,
        packageCoverageStatus,
        paymentStatus: outstandingBalance <= 0 ? 'paid' : 'partially_paid',
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking package coverage notification failed:', error);
  }

  if ((candidateMembership.remainingSessions - consumption.sessionsConsumed) <= 1 || (candidateMembership.remainingCredits - consumption.creditsConsumed) <= 1) {
    try {
      await notifyCreditsLow(db, {
        membership: {
          ...candidateMembership,
          remainingSessions: Math.max(candidateMembership.remainingSessions - consumption.sessionsConsumed, 0),
          remainingCredits: Math.max(candidateMembership.remainingCredits - consumption.creditsConsumed, 0),
        },
        servicePackage,
        actorUserId,
        actorName,
      });
    } catch (error) {
      console.warn('Low-coverage notification failed:', error);
    }
  }

  if (outstandingBalance > 0 && (candidateMembership.remainingSessions - consumption.sessionsConsumed) <= 0 && (candidateMembership.remainingCredits - consumption.creditsConsumed) <= 0) {
    try {
      await notifyPackageCoverageExhausted(db, {
        membership: candidateMembership,
        servicePackage,
        actorUserId,
        actorName,
      });
    } catch (error) {
      console.warn('Coverage exhausted notification failed:', error);
    }
  }

  return {
    bookingId: booking.bookingId || booking.id || '',
    packageId: servicePackage.servicePackageId,
    customerMembershipId: candidateMembership.customerMembershipId,
    packageCoverageAmount: amountCovered,
    packageCoverageStatus,
    outstandingBalance,
  };
}

export async function releasePackageCoverageFromBooking(
  db,
  {
    booking = {},
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const bookingId = booking.bookingId || booking.id || '';
  if (!bookingId) throw new Error('Booking id is required.');

  const usages = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.PACKAGE_USAGES,
    field: 'bookingId',
    value: bookingId,
  });
  const activeUsages = usages
    .map((usage) => normalizePackageUsage(usage))
    .filter((usage) => usage.status === 'applied');

  await Promise.all(
    activeUsages.map(async (usage) => {
      if (usage.customerMembershipId) {
        const membershipSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.CUSTOMER_MEMBERSHIPS, usage.customerMembershipId));
        if (membershipSnapshot.exists()) {
          const membership = normalizeCustomerMembership({ id: membershipSnapshot.id, ...membershipSnapshot.data() });
          await updateCustomerMembershipRemaining(db, membership, {
            remainingSessions: membership.remainingSessions + usage.sessionsConsumed,
            remainingCredits: membership.remainingCredits + usage.creditsConsumed,
          });
        }
      }

      await updateDoc(doc(db, PLATFORM_COLLECTIONS.PACKAGE_USAGES, usage.packageUsageId), {
        status: 'released',
        releasedAt: serverTimestamp(),
      });
    })
  );

  const commercialState = getBookingCommercialState(booking);
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), {
    billingSourceType: commercialState.billingSourceType === 'package' ? '' : commercialState.billingSourceType,
    billingSourceId: commercialState.billingSourceType === 'package' ? '' : commercialState.billingSourceId,
    packageId: '',
    customerMembershipId: '',
    packageCoverageAmount: 0,
    packageCoverageStatus: 'none',
    paymentStatus: commercialState.priceSnapshot > 0 ? 'unpaid' : commercialState.paymentStatus,
    outstandingBalance: commercialState.priceSnapshot || 0,
    updatedAt: serverTimestamp(),
  });

  if (toDateValue(booking.endAt) && toDateValue(booking.endAt)?.getTime() - Date.now() < 1000 * 60 * 60 * 24 * 7) {
    const servicePackageId = activeUsages[0]?.packageId;
    if (servicePackageId) {
      const servicePackageSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.SERVICE_PACKAGES, servicePackageId));
      if (servicePackageSnapshot.exists()) {
        try {
          await notifyMembershipExpiringSoon(db, {
            membership: activeUsages[0],
            servicePackage: normalizeServicePackage({ id: servicePackageSnapshot.id, ...servicePackageSnapshot.data() }),
            actorUserId,
            actorName,
          });
        } catch (error) {
          console.warn('Membership-expiring notification failed:', error);
        }
      }
    }
  }

  return {
    bookingId,
    releasedUsageCount: activeUsages.length,
  };
}
